import { useCallback, useEffect, useMemo, useState } from "react";

import { LoginForm } from "@/components/login-form";
import { AppShell } from "@/components/app-shell";
import { CryptoDashboard } from "@/components/crypto-dashboard";
import { OfflineSnapshotWriter } from "@/components/offline-snapshot-writer";
import { CryptoPanel, ManufacturingPanel, MarketsPanel, OverviewPanel } from "@/components/module-panels";
import { SystemStatusPanel } from "@/components/system-status-panel";
import { getDashboardLoadResult, type DashboardLoadResult } from "@/lib/api";
import { DATA_REFRESHED_EVENT } from "@/lib/data-refresh";
import { offlineSnapshotFromDashboard } from "@/lib/offline-snapshot";


type SessionState = "checking" | "authenticated" | "anonymous" | "unreachable";

const DATA_REFRESH_INTERVAL_MS = 60_000;

function currentLanguage() {
  return document.cookie.split(";").some((entry) => entry.trim() === "hc_lang=uk") ? "uk" : "en";
}

async function readSession(): Promise<SessionState> {
  try {
    const response = await fetch("/api/auth/session", {
      cache: "no-store",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    return response.ok ? "authenticated" : "anonymous";
  } catch {
    return "unreachable";
  }
}

function LoadingScreen({ message = "Opening terminal…" }: { message?: string }) {
  return (
    <main className="grid min-h-dvh place-items-center bg-[var(--hc-bg)] px-4 text-[var(--hc-text)]">
      <p role="status" className="text-sm text-[var(--hc-muted)]">{message}</p>
    </main>
  );
}

function DashboardRoute({ macro }: { macro: boolean }) {
  const [result, setResult] = useState<DashboardLoadResult | null>(null);

  const refresh = useCallback(async () => {
    const next = await getDashboardLoadResult();
    setResult(next);
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), DATA_REFRESH_INTERVAL_MS);
    const onRefresh = () => void refresh();
    const onVisibility = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    window.addEventListener(DATA_REFRESHED_EVENT, onRefresh);
    window.addEventListener("focus", onRefresh);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(DATA_REFRESHED_EVENT, onRefresh);
      window.removeEventListener("focus", onRefresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh]);

  const snapshot = useMemo(
    () => result ? offlineSnapshotFromDashboard(result.data, result.provenance) : null,
    [result],
  );

  if (!result) return <LoadingScreen message="Loading the latest cached view…" />;

  const data = result.data;
  if (macro) {
    return (
      <AppShell collectorRun={data.collector.run} initialLang={currentLanguage()} title="Macro Intelligence" kicker="Global cycle and policy">
        <OfflineSnapshotWriter snapshot={snapshot} />
        <div className="space-y-10">
          <OverviewPanel data={data} />
          <ManufacturingPanel data={data.manufacturing} />
          <MarketsPanel data={data.markets} />
          <CryptoPanel data={data.crypto} />
          <SystemStatusPanel data={data.collector} />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell collectorRun={data.collector.run} initialLang={currentLanguage()} title="Crypto Liquidity" kicker="Digital asset intelligence">
      <OfflineSnapshotWriter snapshot={snapshot} />
      <CryptoDashboard markets={data.markets} crypto={data.crypto} />
    </AppShell>
  );
}

export function App() {
  const [session, setSession] = useState<SessionState>("checking");
  const path = window.location.pathname;
  const loginRoute = path === "/login";

  useEffect(() => {
    void readSession().then(setSession);
  }, []);

  useEffect(() => {
    if (loginRoute && session === "authenticated") window.location.replace("/");
    if (!loginRoute && session === "anonymous") window.location.replace("/login");
  }, [loginRoute, session]);

  if (loginRoute) {
    if (session === "authenticated") return <LoadingScreen />;
    return <LoginForm />;
  }
  if (session === "checking") return <LoadingScreen />;
  if (session === "anonymous") return <LoadingScreen message="Redirecting to sign in…" />;

  // If the session probe cannot reach the server, keep rendering the shell so
  // every module can fall back to the last server cache or its truthful empty state.
  return <DashboardRoute macro={path.startsWith("/macro")} />;
}
